/**
 * The ions the solubility tools recognise, and a decomposition of an ionic formula into
 * cation × n and anion × m.
 *
 * A compound such as Pb(NO₃)₂ is read with the shared formula parser, then matched against
 * every cation/anion pair whose atoms and charges add up to it. The list is the one a
 * general-chemistry course uses; a formula built from other ions (Fe₃O₄, a complex ion,
 * an organic salt) is reported as not recognised rather than guessed.
 */

import {
  countAtoms,
  type ElementCount,
  formulaToUnicode,
  parseFormula,
} from "@/lib/tool-kit/chemistry/formula";

export type IonId =
  | "Li"
  | "Na"
  | "K"
  | "Rb"
  | "Cs"
  | "NH4"
  | "Mg"
  | "Ca"
  | "Sr"
  | "Ba"
  | "Al"
  | "Fe2"
  | "Fe3"
  | "Cu1"
  | "Cu2"
  | "Zn"
  | "Ag"
  | "Pb"
  | "Hg2"
  | "Hg"
  | "Ni"
  | "Co"
  | "Mn"
  | "Cr3"
  | "Sn"
  | "Cd"
  | "F"
  | "Cl"
  | "Br"
  | "I"
  | "NO3"
  | "ClO3"
  | "ClO4"
  | "MnO4"
  | "CH3COO"
  | "HCO3"
  | "HSO4"
  | "SO4"
  | "SO3"
  | "CO3"
  | "C2O4"
  | "CrO4"
  | "PO4"
  | "OH"
  | "S"
  | "O";

export interface Ion {
  id: IonId;
  /** Formula as the parser reads it, without charge: "NO3", "Hg2". */
  formula: string;
  /** Signed charge: +2, −1. */
  charge: number;
  /** Atom counts of one ion. */
  counts: readonly ElementCount[];
  /** Unicode with charge: "NO₃⁻", "Hg₂²⁺". */
  text: string;
  /** More than one atom, so a subscript on it needs brackets: (NO₃)₂. */
  polyatomic: boolean;
}

type Row = readonly [IonId, string, number];

const CATIONS: readonly Row[] = [
  ["Li", "Li", 1],
  ["Na", "Na", 1],
  ["K", "K", 1],
  ["Rb", "Rb", 1],
  ["Cs", "Cs", 1],
  ["NH4", "NH4", 1],
  ["Mg", "Mg", 2],
  ["Ca", "Ca", 2],
  ["Sr", "Sr", 2],
  ["Ba", "Ba", 2],
  ["Al", "Al", 3],
  ["Fe2", "Fe", 2],
  ["Fe3", "Fe", 3],
  ["Cu1", "Cu", 1],
  ["Cu2", "Cu", 2],
  ["Zn", "Zn", 2],
  ["Ag", "Ag", 1],
  ["Pb", "Pb", 2],
  ["Hg2", "Hg2", 2],
  ["Hg", "Hg", 2],
  ["Ni", "Ni", 2],
  ["Co", "Co", 2],
  ["Mn", "Mn", 2],
  ["Cr3", "Cr", 3],
  ["Sn", "Sn", 2],
  ["Cd", "Cd", 2],
];

const ANIONS: readonly Row[] = [
  ["F", "F", -1],
  ["Cl", "Cl", -1],
  ["Br", "Br", -1],
  ["I", "I", -1],
  ["NO3", "NO3", -1],
  ["ClO3", "ClO3", -1],
  ["ClO4", "ClO4", -1],
  ["MnO4", "MnO4", -1],
  ["CH3COO", "CH3COO", -1],
  ["HCO3", "HCO3", -1],
  ["HSO4", "HSO4", -1],
  ["SO4", "SO4", -2],
  ["SO3", "SO3", -2],
  ["CO3", "CO3", -2],
  ["C2O4", "C2O4", -2],
  ["CrO4", "CrO4", -2],
  ["PO4", "PO4", -3],
  ["OH", "OH", -1],
  ["S", "S", -2],
  ["O", "O", -2],
];

function chargeSuffix(charge: number): string {
  const size = Math.abs(charge);
  return `${size === 1 ? "" : size}${charge > 0 ? "+" : "-"}`;
}

function build([id, formula, charge]: Row): Ion {
  const raw = `${formula}^${chargeSuffix(charge)}`;
  const parsed = parseFormula(raw);
  if (!parsed.ok) {
    throw new Error(`ion ${raw} does not parse`);
  }
  const counts = countAtoms(parsed.formula.parts.flatMap((p) => p.nodes));
  return {
    id,
    formula,
    charge,
    counts,
    text: formulaToUnicode(parsed.formula),
    polyatomic: counts.reduce((total, c) => total + c.count, 0) > 1,
  };
}

export const CATION_LIST: readonly Ion[] = CATIONS.map(build);
export const ANION_LIST: readonly Ion[] = ANIONS.map(build);
const ALL = new Map(
  [...CATION_LIST, ...ANION_LIST].map((ion) => [ion.id, ion])
);

export function getIon(id: IonId): Ion {
  const ion = ALL.get(id);
  if (!ion) {
    throw new Error(`unknown ion ${id}`);
  }
  return ion;
}

const MAX_SUBSCRIPT = 6;
const STATE_SUFFIX = /\s*\((?:aq|s|l|g)\)\s*$/;

export interface Salt {
  cation: Ion;
  anion: Ion;
  /** How many cations and anions in one formula unit. */
  nCation: number;
  nAnion: number;
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

function atomsOf(formula: string): Map<string, number> | null {
  const parsed = parseFormula(formula.replace(STATE_SUFFIX, ""));
  if (!parsed.ok || parsed.formula.charge !== 0) {
    return null;
  }
  const [first, ...rest] = parsed.formula.parts;
  // A hydrate such as CaSO4·2H2O is the salt plus water of crystallisation.
  const onlyWater = rest.every((part) => {
    const counts = countAtoms(part.nodes);
    return (
      counts.length === 2 &&
      counts.some((c) => c.symbol === "H" && c.count === 2) &&
      counts.some((c) => c.symbol === "O" && c.count === 1)
    );
  });
  if (!(first && onlyWater)) {
    return null;
  }
  return new Map(
    countAtoms(first.nodes, first.coefficient).map((c) => [c.symbol, c.count])
  );
}

function matches(
  target: Map<string, number>,
  cation: Ion,
  anion: Ion,
  n: number,
  m: number
): boolean {
  const total = new Map<string, number>();
  for (const { symbol, count } of cation.counts) {
    total.set(symbol, (total.get(symbol) ?? 0) + count * n);
  }
  for (const { symbol, count } of anion.counts) {
    total.set(symbol, (total.get(symbol) ?? 0) + count * m);
  }
  if (total.size !== target.size) {
    return false;
  }
  for (const [symbol, count] of target) {
    if (total.get(symbol) !== count) {
      return false;
    }
  }
  return true;
}

/** Every way the formula splits into one cation and one anion. Usually zero or one. */
export function decomposeAll(formula: string): Salt[] {
  const target = atomsOf(formula);
  if (!target) {
    return [];
  }
  const found: Salt[] = [];
  for (const cation of CATION_LIST) {
    for (const anion of ANION_LIST) {
      const divisor = gcd(cation.charge, -anion.charge);
      // Smallest charge-neutral ratio; the formula may be a multiple of it only if
      // the subscripts are written that way (never for the ions here).
      const n = -anion.charge / divisor;
      const m = cation.charge / divisor;
      if (
        n <= MAX_SUBSCRIPT &&
        m <= MAX_SUBSCRIPT &&
        matches(target, cation, anion, n, m)
      ) {
        found.push({ cation, anion, nCation: n, nAnion: m });
      }
    }
  }
  return found;
}

/** The salt a formula describes, or null when it is not a salt of the listed ions. */
export function decompose(formula: string): Salt | null {
  const all = decomposeAll(formula);
  return all.length === 1 ? all[0] : (all[0] ?? null);
}

/** The formula of cation × n with anion × m, in Unicode: Pb(NO₃)₂, Hg₂Cl₂, Ca₃(PO₄)₂. */
export function saltFormula(salt: Salt): string {
  const part = (ion: Ion, count: number) => {
    const sub = count === 1 ? "" : String(count);
    return ion.polyatomic && count > 1
      ? `(${ion.formula})${sub}`
      : `${ion.formula}${sub}`;
  };
  const ascii = part(salt.cation, salt.nCation) + part(salt.anion, salt.nAnion);
  const parsed = parseFormula(ascii);
  return parsed.ok ? formulaToUnicode(parsed.formula) : ascii;
}

/** Build the salt of a cation and an anion in the smallest charge-balanced ratio. */
export function makeSalt(cation: Ion, anion: Ion): Salt {
  const divisor = gcd(cation.charge, -anion.charge);
  return {
    cation,
    anion,
    nCation: -anion.charge / divisor,
    nAnion: cation.charge / divisor,
  };
}
